import { Swiper, FreeMode, Navigation, Thumbs } from '../vendor/swiper.js';
import { destroyPdpGalleryZoom, initPdpGalleryZoom } from './detail-gallery-zoom.js';

export const galleryState = new WeakMap();
/** Keep in sync with `_pdp-gallery.scss` thumbs breakpoint. */
export const PDP_MOBILE_MQ = '(max-width: 992px)';

export function isPdpMobile() {
  return typeof window !== 'undefined' && window.matchMedia(PDP_MOBILE_MQ).matches;
}

/**
 * Same shell + SVG arrows as promote / «Другие букеты» (products-swiper-shell).
 *
 * @param {HTMLElement} thumbsEl
 * @returns {{
 *   shell: HTMLElement,
 *   prevEl: HTMLButtonElement,
 *   nextEl: HTMLButtonElement
 * }}
 */
function setupThumbsNavigation(thumbsEl) {
  const existingShell = thumbsEl.closest('.products-swiper-shell');

  if (existingShell instanceof HTMLElement) {
    const existingPrev = existingShell.querySelector(':scope > .swiper-button-prev');
    const existingNext = existingShell.querySelector(':scope > .swiper-button-next');

    if (
      existingPrev instanceof HTMLButtonElement
      && existingNext instanceof HTMLButtonElement
    ) {
      return {
        shell: existingShell,
        prevEl: existingPrev,
        nextEl: existingNext,
      };
    }
  }

  const shell = document.createElement('div');
  shell.className = 'products-swiper-shell';
  thumbsEl.parentNode.insertBefore(shell, thumbsEl);
  shell.appendChild(thumbsEl);

  const prevEl = document.createElement('button');
  prevEl.type = 'button';
  prevEl.className = 'swiper-button-prev';
  prevEl.setAttribute('aria-label', 'Назад');
  prevEl.innerHTML = `
      <svg width="6" height="12" aria-hidden="true" focusable="false">
          <use href="#icon-arrow-left" xlink:href="#icon-arrow-left"></use>
      </svg>
    `;

  const nextEl = document.createElement('button');
  nextEl.type = 'button';
  nextEl.className = 'swiper-button-next';
  nextEl.setAttribute('aria-label', 'Вперёд');
  nextEl.innerHTML = `
      <svg width="6" height="12" aria-hidden="true" focusable="false">
          <use href="#icon-arrow-right" xlink:href="#icon-arrow-right"></use>
      </svg>
    `;

  shell.append(prevEl, nextEl);

  return { shell, prevEl, nextEl };
}

export function bindSlideImageFade(wrapper) {
  wrapper.querySelectorAll(':scope img').forEach((image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const markReady = () => {
      image.dataset.pdpLoaded = '1';
    };

    if (image.complete && image.naturalWidth > 0) {
      markReady();
      return;
    }

    image.dataset.pdpLoaded = '0';
    image.addEventListener('load', markReady, { once: true });
    image.addEventListener('error', markReady, { once: true });
  });
}

export function prepareMainGallery(container) {
  if (!(container instanceof HTMLElement)) return null;

  const wrapper = container.querySelector(':scope > .slides, :scope > ul.slides, :scope > .swiper-wrapper');
  if (!(wrapper instanceof HTMLElement)) return null;

  // Stickers / other siblings must not participate in Swiper track layout.
  Array.from(container.children).forEach((child) => {
    if (child === wrapper) return;
    if (child.classList.contains('stickers') || child.classList.contains('mob_stickers')) {
      child.style.position = 'absolute';
      child.style.zIndex = '2';
    }
  });

  container.classList.add('swiper', 'pdp-swiper', 'pdp-gallery');
  wrapper.classList.add('swiper-wrapper');
  wrapper.querySelectorAll(':scope > li').forEach((slide) => {
    slide.classList.add('swiper-slide');
    slide.classList.remove('current');
    slide.removeAttribute('style');
  });
  bindSlideImageFade(wrapper);

  return wrapper;
}

export function prepareThumbs(container) {
  if (!(container instanceof HTMLElement)) return null;
  const wrapper = container.querySelector(':scope > .slides_block, :scope > .swiper-wrapper');
  if (!(wrapper instanceof HTMLElement)) return null;

  // Drop legacy flexslider max-width so the strip fills the column.
  container.style.removeProperty('max-width');

  container.classList.add('swiper', 'pdp-swiper');
  wrapper.classList.add('swiper-wrapper');
  wrapper.querySelectorAll(':scope > li').forEach((slide) => {
    slide.classList.add('swiper-slide');
    slide.removeAttribute('style');
  });
  bindSlideImageFade(wrapper);
  return wrapper;
}

export function initThumbsGallery(root) {
  // Mobile uses main gallery + pagination only — no thumbs strip.
  if (isPdpMobile()) return null;

  const thumbsContainer = root.querySelector('.wrapp_thumbs .thumbs');
  const thumbsWrapper = prepareThumbs(thumbsContainer);
  if (!thumbsContainer || !thumbsWrapper || thumbsWrapper.children.length < 2) return null;

  const { prevEl, nextEl } = setupThumbsNavigation(thumbsContainer);

  return new Swiper(thumbsContainer, {
    modules: [FreeMode, Navigation, Thumbs],
    slidesPerView: 'auto',
    spaceBetween: 4,
    freeMode: true,
    watchOverflow: true,
    watchSlidesProgress: true,
    observer: true,
    observeParents: true,
    navigation: {
      prevEl,
      nextEl,
    },
  });
}

/** @type {WeakMap<HTMLElement, () => void>} */
const nativeGalleryCleanups = new WeakMap();

export function bindNativePdpGallery(container, wrapper, root) {
  container.classList.add('is-native-scroll');
  const slideCount = Array.from(wrapper.children).filter(
    (child) => child instanceof HTMLElement,
  ).length;
  container.classList.toggle('is-pdp-gallery-single', slideCount === 1);

  const sync = () => {
    const slides = Array.from(wrapper.children).filter(
      (child) => child instanceof HTMLElement && child.classList.contains('swiper-slide'),
    );
    if (!slides.length) {
      return;
    }

    const x = container.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((slide, index) => {
      const dist = Math.abs(slide.offsetLeft - x);
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });
    slides.forEach((slide, index) => {
      slide.classList.toggle('swiper-slide-active', index === best);
    });
    resetMobileVideo(root);
  };

  container.addEventListener('scroll', sync, { passive: true });
  container.scrollLeft = 0;
  container.style.removeProperty('opacity');
  container.setAttribute('data-pdp-gallery-ready', '1');
  wrapper.querySelectorAll('img').forEach((image) => {
    if (image instanceof HTMLImageElement) {
      image.dataset.pdpLoaded = '1';
    }
  });
  window.requestAnimationFrame(() => {
    container.scrollLeft = 0;
    sync();
  });

  nativeGalleryCleanups.set(container, () => {
    container.removeEventListener('scroll', sync);
    container.classList.remove('is-native-scroll', 'is-pdp-gallery-single');
  });
}

export function resetMobileVideo(root) {
  root.querySelectorAll('.video_offers_mobile_wrapper').forEach((el) => {
    el.style.display = 'none';
  });
  root.querySelectorAll('a.offers_video_mobile').forEach((el) => {
    el.style.display = '';
  });
  root.querySelectorAll('button.offers_video_mobile.bty').forEach((el) => {
    el.style.display = 'block';
  });
  root.querySelectorAll('video.player').forEach((video) => {
    if (typeof video.pause === 'function') video.pause();
  });
}

export function destroyGallery(root) {
  const state = galleryState.get(root);
  const nativeEl = root.querySelector('.item_slider.is-native-scroll');
  if (nativeEl instanceof HTMLElement) {
    nativeGalleryCleanups.get(nativeEl)?.();
    nativeGalleryCleanups.delete(nativeEl);
  }
  if (!state) return;
    destroyPdpGalleryZoom();
    state.mainSwiper?.destroy(true, true);
  state.thumbsSwiper?.destroy(true, true);
  galleryState.delete(root);
}

/**
 * @param {(root: Element, thumbsSwiper: import('swiper').default | null) => import('swiper').default | null} initMainGallery
 */
export function createInitDetailGallery(initMainGallery) {
  function initScope(root) {
    destroyGallery(root);

    const thumbsSwiper = initThumbsGallery(root);
    const mainSwiper = initMainGallery(root, thumbsSwiper);

    if (mainSwiper && thumbsSwiper) {
      const syncCurrent = () => {
        thumbsSwiper.slides.forEach((slide, index) => {
          slide.classList.toggle('current', index === mainSwiper.activeIndex);
        });
        const activeThumb = thumbsSwiper.slides[mainSwiper.activeIndex];
        if (!(activeThumb instanceof HTMLElement) || !activeThumb.classList.contains('video_thumb')) {
          root.querySelectorAll('.offers_video').forEach((el) => {
            el.style.display = 'none';
          });
        }
      };
      mainSwiper.on('slideChange', syncCurrent);
      syncCurrent();
    }

    galleryState.set(root, { mainSwiper, thumbsSwiper });
    initPdpGalleryZoom();

    requestAnimationFrame(() => {
      mainSwiper?.update();
      thumbsSwiper?.update();
      // Do not force-reveal here: SKU switch keeps skeleton until first image loads
      // (SetSliderPictMobile). Noffer / already-ready nodes stay as-is.
      revealGalleryWhenImagesReady(root);
    });
  }

  function revealGalleryWhenImagesReady(root) {
    const pending = Array.from(root.querySelectorAll('[data-pdp-gallery-ready="0"]'));
    if (!pending.length) return;

    const main = root.querySelector('.item_slider.flex');
    const thumbsWrap = root.querySelector('.wrapp_thumbs');
    const firstImg = main instanceof HTMLElement ? main.querySelector('img') : null;
    const firstThumb =
      thumbsWrap instanceof HTMLElement ? thumbsWrap.querySelector('img') : null;

    const reveal = () => {
      if (main instanceof HTMLElement) {
        main.classList.add('is-pdp-snap-reveal');
      }
      if (thumbsWrap instanceof HTMLElement) {
        thumbsWrap.classList.add('is-pdp-snap-reveal');
      }
      if (firstImg instanceof HTMLImageElement) {
        firstImg.dataset.pdpLoaded = '1';
      }
      if (thumbsWrap instanceof HTMLElement) {
        thumbsWrap.querySelectorAll('img').forEach((img) => {
          if (img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0) {
            img.dataset.pdpLoaded = '1';
          }
        });
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          pending.forEach((el) => {
            if (el.getAttribute('data-pdp-gallery-ready') === '0') {
              el.setAttribute('data-pdp-gallery-ready', '1');
            }
          });
          if (main instanceof HTMLElement && main.classList.contains('is-native-scroll')) {
            main.scrollLeft = 0;
          }
          requestAnimationFrame(() => {
            if (main instanceof HTMLElement) {
              main.classList.remove('is-pdp-snap-reveal');
            }
            if (thumbsWrap instanceof HTMLElement) {
              thumbsWrap.classList.remove('is-pdp-snap-reveal');
            }
          });
        });
      });
    };

    const images = [firstImg, firstThumb].filter(
      (img) => img instanceof HTMLImageElement,
    );
    if (!images.length) {
      reveal();
      return;
    }

    let left = images.length;
    let settled = false;
    const settle = () => {
      if (settled) return;
      left -= 1;
      if (left <= 0) {
        settled = true;
        reveal();
      }
    };

    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        settle();
      } else {
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    });

    setTimeout(() => {
      if (!settled) {
        settled = true;
        reveal();
      }
    }, 4000);
  }

  function initDetailGallery(root = document) {
    const host = root instanceof Document ? root : root.ownerDocument || document;
    host.querySelectorAll('.catalog_detail .item_main_info').forEach((scope) => initScope(scope));
  }

  document.addEventListener('pdp-gallery:refresh', () => initDetailGallery(document));

  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia(PDP_MOBILE_MQ);
    const onBreakpoint = () => initDetailGallery(document);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onBreakpoint);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onBreakpoint);
    }
  }

  return initDetailGallery;
}
